/**
 * SeoAdmin ,  /app/admin/seo (Iter 56).
 *
 * Three panels:
 *   1. Per-route meta editor with character-count guidance and live SERP +
 *      social-card preview
 *   2. On-demand SEO audit ,  pass / warn / fail per route
 *   3. Sitemap panel ,  cached-at timestamp, Regenerate Now button, live
 *      sitemap URL, manual-submission links (we do NOT fake ping automation)
 *   +  Google Search Console verification field (auto-injected on homepage)
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  RefreshCw, Save, Search, ExternalLink, AlertTriangle, CheckCircle2,
  XCircle, ClipboardCopy, Globe, Info,
} from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Character-count guidance (Google/Bing SERP display)
const TITLE_MIN = 40;
const TITLE_MAX = 65;
const DESC_MIN = 120;
const DESC_MAX = 170;

function counterColor(len, min, max) {
  if (!len) return "text-slate-500";
  if (len < min || len > max) return "text-amber-400";
  return "text-emerald-400";
}

function severityBadge(status) {
  const map = {
    pass: { text: "PASS", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", Icon: CheckCircle2 },
    warn: { text: "WARN", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40", Icon: AlertTriangle },
    fail: { text: "FAIL", cls: "bg-rose-500/15 text-rose-300 border-rose-500/40", Icon: XCircle },
  };
  const m = map[status] || map.warn;
  const I = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border font-mono ${m.cls}`}>
      <I className="h-3 w-3" /> {m.text}
    </span>
  );
}

export default function SeoAdmin() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // route string
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const [auditResult, setAuditResult] = useState(null);
  const [runningAudit, setRunningAudit] = useState(false);

  const [sitemapInfo, setSitemapInfo] = useState(null);

  const [verification, setVerification] = useState("");
  const [savingVerification, setSavingVerification] = useState(false);

  const load = () => {
    api.get("/seo/meta").then((r) => setRows(r.data));
    api.get("/admin/seo/sitemap/info").then((r) => setSitemapInfo(r.data));
    api.get("/seo/verification").then((r) => setVerification(r.data.google_verification || ""));
  };

  useEffect(load, []);

  const startEdit = (row) => {
    setEditing(row.route);
    setDraft({
      title: row.title || "",
      description: row.description || "",
      og_image_url: row.og_image_url || "",
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft({});
  };

  const save = async () => {
    setSaving(true);
    try {
      const route = editing.replace(/^\//, "");
      const r = await api.patch(`/admin/seo/meta/${route}`, draft);
      setRows((prev) => prev.map((p) => (p.route === editing ? { ...p, ...r.data } : p)));
      toast.success(`Saved SEO meta for ${editing}`);
      cancelEdit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const runAudit = async () => {
    setRunningAudit(true);
    try {
      const r = await api.post("/admin/seo/audit");
      setAuditResult(r.data);
      toast.success(`Audit complete ,  ${r.data.summary.pass} pass · ${r.data.summary.warn} warn · ${r.data.summary.fail} fail`);
    } catch (e) {
      toast.error("Audit failed");
    } finally {
      setRunningAudit(false);
    }
  };

  const regenerateSitemap = async () => {
    try {
      await api.post("/admin/seo/sitemap/regenerate");
      toast.success("Sitemap cache invalidated ,  next crawler hit rebuilds");
      api.get("/admin/seo/sitemap/info").then((r) => setSitemapInfo(r.data));
    } catch {
      toast.error("Failed to regenerate");
    }
  };

  const saveVerification = async () => {
    setSavingVerification(true);
    try {
      await api.patch("/admin/seo/verification", { google_verification: verification });
      toast.success("Verification tag saved");
    } catch (e) {
      toast.error("Failed to save verification tag");
    } finally {
      setSavingVerification(false);
    }
  };

  const copyToClipboard = (t, label) => {
    navigator.clipboard.writeText(t);
    toast.success(`${label} copied`);
  };

  return (
    <div className="space-y-8" data-testid="seo-admin-page">
      <header>
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Admin · SEO</div>
        <h1 className="font-heading text-4xl tracking-tight">Search & Social Presence</h1>
        <p className="text-sm text-slate-400 mt-1">
          Control the title, description, and social-card image every public route serves. Run an on-demand audit and manage the sitemap.
        </p>
      </header>

      {/* ============================================================
          PANEL 1 ,  Per-route editor
         ============================================================ */}
      <div className="ae-card p-6" data-testid="seo-editor-panel">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400">Panel 1</div>
            <h2 className="font-heading text-2xl tracking-tight">Per-route Editor</h2>
          </div>
          <div className="text-[11px] text-slate-500">
            {rows.length} public routes
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/8">
                <th className="text-left px-3 py-2">Route</th>
                <th className="text-left px-3 py-2">Title</th>
                <th className="text-left px-3 py-2">Description</th>
                <th className="text-right px-3 py-2">Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isEditing = editing === row.route;
                return (
                  <>
                    <tr key={row.route} className="border-b border-white/5" data-testid={`seo-row-${row.route}`}>
                      <td className="px-3 py-3 font-mono text-xs text-emerald-400">{row.route}</td>
                      <td className="px-3 py-3 text-slate-200 max-w-md">
                        <div className="truncate" title={row.title}>{row.title}</div>
                        <div className={`text-[10px] mt-0.5 ${counterColor(row.title?.length, TITLE_MIN, TITLE_MAX)}`}>{row.title?.length || 0} chars</div>
                      </td>
                      <td className="px-3 py-3 text-slate-300 max-w-lg">
                        <div className="truncate text-xs" title={row.description}>{row.description}</div>
                        <div className={`text-[10px] mt-0.5 ${counterColor(row.description?.length, DESC_MIN, DESC_MAX)}`}>{row.description?.length || 0} chars</div>
                      </td>
                      <td className="px-3 py-3 text-right text-[11px] font-mono text-slate-500">
                        {row.updated_at ? fmtDateTime(row.updated_at) : ", "}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <button
                          onClick={() => (isEditing ? cancelEdit() : startEdit(row))}
                          className="text-xs text-emerald-400 hover:text-emerald-300"
                          data-testid={`seo-edit-btn-${row.route}`}
                        >
                          {isEditing ? "Cancel" : "Edit"}
                        </button>
                      </td>
                    </tr>
                    {isEditing && (
                      <tr>
                        <td colSpan={5} className="px-3 pb-6 bg-white/[0.02]">
                          <EditForm
                            route={row.route}
                            draft={draft}
                            setDraft={setDraft}
                            saving={saving}
                            onSave={save}
                            onCancel={cancelEdit}
                          />
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ============================================================
          PANEL 2 ,  Audit
         ============================================================ */}
      <div className="ae-card p-6" data-testid="seo-audit-panel">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400">Panel 2</div>
            <h2 className="font-heading text-2xl tracking-tight">Health Check</h2>
            <p className="text-xs text-slate-500 mt-1">Crawls every public route server-side and reports duplicate/missing titles, off-range lengths, and infrastructure reachability.</p>
          </div>
          <button
            onClick={runAudit}
            disabled={runningAudit}
            data-testid="seo-run-audit-btn"
            className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-40"
          >
            <Search className="h-4 w-4" /> {runningAudit ? "Running…" : "Run SEO Audit"}
          </button>
        </div>

        {auditResult ? (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              <SummaryTile label="Pass" value={auditResult.summary.pass} color="emerald" />
              <SummaryTile label="Warn" value={auditResult.summary.warn} color="amber" />
              <SummaryTile label="Fail" value={auditResult.summary.fail} color="rose" />
              <div className="ae-card p-4 border-emerald-500/20">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Infrastructure</div>
                <div className="mt-2 text-xs space-y-1">
                  <InfraRow label="sitemap.xml" info={auditResult.infra.sitemap} />
                  <InfraRow label="robots.txt" info={auditResult.infra.robots} />
                </div>
              </div>
            </div>

            <div className="border border-white/8 rounded-sm">
              {auditResult.routes.map((r) => (
                <div key={r.route} className="border-b border-white/8 last:border-0 p-3" data-testid={`audit-row-${r.route}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs text-emerald-400 min-w-[160px]">{r.route}</span>
                    {severityBadge(r.status)}
                    <span className="text-[11px] text-slate-500 font-mono">title:{r.title_len} desc:{r.description_len}</span>
                  </div>
                  {r.issues.length > 0 && (
                    <ul className="mt-2 ml-[172px] text-xs space-y-1">
                      {r.issues.map((i, idx) => (
                        <li key={idx} className={`${i.severity === "fail" ? "text-rose-300" : "text-amber-300"}`}>
                          <span className="text-slate-500 font-mono text-[10px] mr-2">{i.code}</span>{i.msg}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-500 text-center py-10">
            Click <strong className="text-slate-300">Run SEO Audit</strong> to inspect every public route.
          </div>
        )}
      </div>

      {/* ============================================================
          PANEL 3 ,  Sitemap
         ============================================================ */}
      <div className="ae-card p-6" data-testid="seo-sitemap-panel">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400">Panel 3</div>
            <h2 className="font-heading text-2xl tracking-tight">Sitemap & Submissions</h2>
          </div>
          <button onClick={regenerateSitemap} className="btn-ghost text-sm inline-flex items-center gap-2" data-testid="seo-regen-sitemap-btn">
            <RefreshCw className="h-4 w-4" /> Regenerate Now
          </button>
        </div>

        {sitemapInfo && (
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <div className="ae-card p-4 border-emerald-500/20">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Sitemap URL</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs font-mono break-all text-emerald-400">{sitemapInfo.sitemap_url}</code>
                  <button onClick={() => copyToClipboard(sitemapInfo.sitemap_url, "Sitemap URL")} className="text-slate-500 hover:text-slate-200"><ClipboardCopy className="h-3.5 w-3.5" /></button>
                </div>
                <div className="text-[11px] text-slate-500 mt-2 font-mono">
                  Cached at: {sitemapInfo.cached_at ? fmtDateTime(sitemapInfo.cached_at) : "not yet generated"}
                </div>
                <div className="text-[10px] text-slate-600">TTL: {sitemapInfo.cache_ttl_seconds}s</div>
                <div className="mt-3 pt-3 border-t border-white/8">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Last automated audit</div>
                  {sitemapInfo.last_scheduled_audit_at ? (
                    <div data-testid="seo-last-scheduled-audit">
                      <div className="text-xs font-mono text-emerald-400">{fmtDateTime(sitemapInfo.last_scheduled_audit_at)}</div>
                      {sitemapInfo.last_scheduled_audit_summary && (
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          {sitemapInfo.last_scheduled_audit_summary.pass} pass · {sitemapInfo.last_scheduled_audit_summary.warn} warn · {sitemapInfo.last_scheduled_audit_summary.fail} fail
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-[11px] text-slate-500 italic" data-testid="seo-last-scheduled-audit-empty">
                      No automated run yet ,  next Monday 03:15 UTC
                    </div>
                  )}
                </div>
              </div>
              <div className="ae-card p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">robots.txt URL</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs font-mono break-all text-slate-300">{sitemapInfo.robots_url}</code>
                  <button onClick={() => copyToClipboard(sitemapInfo.robots_url, "robots URL")} className="text-slate-500 hover:text-slate-200"><ClipboardCopy className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>

            <div className="ae-card p-4 border-amber-500/20">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-amber-400 mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs font-medium text-amber-300 mb-1">Manual sitemap submission (no automation)</div>
                  <p className="text-[11px] text-slate-400 leading-relaxed mb-3">{sitemapInfo.notes}</p>
                  <div className="flex flex-wrap gap-2">
                    <a href={sitemapInfo.ping_urls.google_search_console} target="_blank" rel="noopener noreferrer" className="text-xs inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300" data-testid="seo-ping-gsc">
                      Google Search Console <ExternalLink className="h-3 w-3" />
                    </a>
                    <a href={sitemapInfo.ping_urls.bing_webmaster_tools} target="_blank" rel="noopener noreferrer" className="text-xs inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300" data-testid="seo-ping-bing">
                      Bing Webmaster Tools <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ============================================================
          Google Search Console verification tag
         ============================================================ */}
      <div className="ae-card p-6" data-testid="seo-verification-panel">
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Panel 4</div>
        <h2 className="font-heading text-2xl tracking-tight mb-2">Search Console Verification</h2>
        <p className="text-xs text-slate-500 mb-4 leading-relaxed">
          Paste the <span className="text-emerald-400 font-mono">content</span> attribute of the meta tag Google Search Console gives you. It will be auto-injected into the homepage &lt;head&gt; on next render ,  no deploy required.
        </p>
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={verification}
            onChange={(e) => setVerification(e.target.value)}
            placeholder="e.g. ABc123DEf456… (the value inside content=&quot;…&quot;)"
            className="flex-1 bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2 text-xs font-mono focus:border-emerald-500/50 focus:outline-none"
            data-testid="seo-verification-input"
          />
          <button onClick={saveVerification} disabled={savingVerification} data-testid="seo-verification-save-btn" className="btn-primary text-xs">
            {savingVerification ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditForm({ route, draft, setDraft, saving, onSave, onCancel }) {
  const titleLen = draft.title?.length || 0;
  const descLen = draft.description?.length || 0;
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div className="grid lg:grid-cols-2 gap-6 mt-3" data-testid={`seo-edit-form-${route}`}>
      <div className="space-y-4">
        <div>
          <div className="flex items-baseline justify-between">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">Title</label>
            <span className={`text-[10px] font-mono ${counterColor(titleLen, TITLE_MIN, TITLE_MAX)}`}>{titleLen}/{TITLE_MAX}</span>
          </div>
          <input
            type="text"
            value={draft.title || ""}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2 text-sm focus:border-emerald-500/50 focus:outline-none"
            data-testid={`seo-title-input-${route}`}
          />
          <div className="text-[10px] text-slate-600 mt-1">Recommended {TITLE_MIN}-{TITLE_MAX} chars for Google SERP.</div>
        </div>
        <div>
          <div className="flex items-baseline justify-between">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">Description</label>
            <span className={`text-[10px] font-mono ${counterColor(descLen, DESC_MIN, DESC_MAX)}`}>{descLen}/{DESC_MAX}</span>
          </div>
          <textarea
            value={draft.description || ""}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            rows={3}
            className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2 text-sm focus:border-emerald-500/50 focus:outline-none"
            data-testid={`seo-desc-input-${route}`}
          />
          <div className="text-[10px] text-slate-600 mt-1">Recommended {DESC_MIN}-{DESC_MAX} chars.</div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">OG Image (absolute URL)</label>
          <input
            type="text"
            value={draft.og_image_url || ""}
            onChange={(e) => setDraft({ ...draft, og_image_url: e.target.value })}
            className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2 text-xs font-mono focus:border-emerald-500/50 focus:outline-none"
            data-testid={`seo-og-input-${route}`}
          />
          <div className="text-[10px] text-slate-600 mt-1">1200×630px, absolute URL.</div>
        </div>
        <div className="flex items-center gap-2 pt-2">
          <button onClick={onSave} disabled={saving} className="btn-primary text-xs inline-flex items-center gap-2" data-testid={`seo-save-btn-${route}`}>
            <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
          </button>
          <button onClick={onCancel} className="btn-ghost text-xs">Cancel</button>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
            <Search className="h-3 w-3" /> Google SERP preview
          </div>
          <div className="bg-white text-slate-900 rounded-sm p-4 space-y-1 shadow-lg" data-testid={`seo-serp-preview-${route}`}>
            <div className="text-xs text-emerald-800">{origin}{route}</div>
            <div className="text-[18px] leading-tight text-blue-800 hover:underline cursor-pointer truncate">
              {(draft.title || "").slice(0, 65)}
            </div>
            <div className="text-[13px] text-slate-600 leading-snug line-clamp-2">
              {(draft.description || "").slice(0, 170)}
            </div>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
            <Globe className="h-3 w-3" /> Social card preview
          </div>
          <div className="rounded-sm border border-slate-700 bg-slate-800 overflow-hidden" data-testid={`seo-social-preview-${route}`}>
            {draft.og_image_url ? (
              <img src={draft.og_image_url} alt="Social card preview" className="w-full h-40 object-cover" />
            ) : (
              <div className="h-40 flex items-center justify-center text-xs text-slate-500 bg-slate-900">No OG image</div>
            )}
            <div className="p-3">
              <div className="text-[10px] uppercase text-slate-500 mb-1 truncate">{origin.replace(/^https?:\/\//, "")}</div>
              <div className="text-sm font-semibold text-slate-100 truncate">{draft.title || ", "}</div>
              <div className="text-xs text-slate-400 line-clamp-2 mt-0.5">{draft.description || ", "}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, color }) {
  const cls = {
    emerald: "text-emerald-400 border-emerald-500/30",
    amber: "text-amber-400 border-amber-500/30",
    rose: "text-rose-400 border-rose-500/30",
  }[color];
  return (
    <div className={`ae-card p-4 ${cls}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-mono tabular-nums text-3xl font-bold mt-1">{value}</div>
    </div>
  );
}

function InfraRow({ label, info }) {
  const ok = info?.reachable && (info.valid_xml !== false);
  return (
    <div className="flex items-center justify-between">
      <span className="font-mono text-[11px] text-slate-400">{label}</span>
      <span className={`font-mono text-[11px] ${ok ? "text-emerald-400" : "text-rose-400"}`}>
        {ok ? "OK" : info?.reachable ? "invalid" : "unreachable"}
      </span>
    </div>
  );
}
